import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { SharingType } from '../../mru/shared-with-me/shared-with-me.model';
import { AnalyticsService } from '../analytics/analytics.service';
import { FetchStrategy } from '../instrumentation/instrumented-http.service';
import { FileType, OneDriveConsumerUrlRegex, SharePointUrlRegex } from '../models/file-item.model';
import { GraphDriveItem, GraphThumbnailSet } from '../models/onedrive-graph.model';
import { MruConfig, MruConfigService } from '../mru/mru-config.service';
import { MruFileFormat, MruFileItem } from '../mru/mru-file-item.model';
import { DateService } from '../services/date.service';
import { FormsService } from '../services/forms.service';
import { OneDriveGraphService } from '../services/onedrive-graph.service';
import { OneDriveService } from '../services/onedrive.service';
import { SchedulePriority } from '../services/scheduler.service';
import { SwayService } from '../services/sway.service';
import { UrlAuthenticatorService } from '../services/url-authenticator.service';
import { VroomService } from '../services/vroom.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { AuthType, ClientStartupConfigService } from './../shared-context/client-startup-config.service';

@Injectable({
    providedIn: 'root'
})
export class MruItemInflationService {
    public readonly thumbnailApiEnabled: boolean;

    private readonly config: MruConfig;
    private readonly lifecycleEventsEnabled: boolean;
    private readonly inflationsInProgress: Array<Promise<MruFileItem>>;
    private readonly inMockMode: boolean;
    private readonly highResRecThumbnails: boolean;

    public static readonly thumbnailFileTypes = new Set([FileType.PowerPoint, FileType.Visio, FileType.Word, FileType.Sway, FileType.Pdf, FileType.Forms, FileType.Stream]);
    public static readonly disableInflation = new Set([FileType.Project, FileType.ProjectOperations, FileType.Portfolio]);

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        contextService: ContextService,
        private readonly dateService: DateService,
        private readonly formsService: FormsService,
        mruConfigService: MruConfigService,
        private readonly oneDriveGraphService: OneDriveGraphService,
        private readonly vroomService: VroomService,
        private readonly swayService: SwayService,
        private readonly analyticsService: AnalyticsService,
        private readonly urlAuthenticatorService: UrlAuthenticatorService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) {
        this.config = mruConfigService.config;
        this.lifecycleEventsEnabled = this.config.isNewLifecycleEventEnabled;
        this.inflationsInProgress = new Array<Promise<MruFileItem>>();
        this.inMockMode = contextService.sharedContext.inMockMode;
        this.highResRecThumbnails = this.capabilitiesService.capabilities.highResRecThumbnailsEnabled;
        this.thumbnailApiEnabled = this.capabilitiesService.capabilities.thumbnailEndpointEnabled;
    }

    public inflateItem(item: MruFileItem, fetchThumbnails: boolean, fetchAnalytics: boolean, mruAppType?: FileType): Promise<MruFileItem> {
        if (item.isLocal || MruItemInflationService.disableInflation.has(item.fileType)) {
            return Promise.resolve(item);
        }

        const strategy = item.fromCache ? FetchStrategy.CacheOnly : FetchStrategy.NetworkFirst;

        const inflate = this.fetchItemInflationData(item, strategy, fetchThumbnails, fetchAnalytics, mruAppType);

        this.inflationsInProgress.push(inflate.then(inflatedItem => inflatedItem, () => null));
        this.waitOnInflations().then(() => this.inflationsInProgress.splice(0));

        return inflate;
    }

    public inflateThumbnails(item: MruFileItem): Promise<MruFileItem> {
        if (!MruItemInflationService.thumbnailFileTypes.has(item.fileType) || item.thumbnails) {
            return Promise.resolve(item);
        }

        const strategy = item.fromCache ? FetchStrategy.CacheOnly : FetchStrategy.NetworkFirst;

        return new Promise<MruFileItem>(resolve => {
            if (item.fileType === FileType.Sway) {
                this.fetchSwayThumbnail(item, strategy)
                    .then(() => {
                        resolve(item);
                    });
                return;
            } else if (item.fileType === FileType.Forms) {
                item.thumbnails = {};
                this.formsService.fetchFormThumbnail(item).then(() => resolve(item));
                return;
            }
            // If ANTP, do not make shares call
            if (!item.driveId || !item.docId) {
                // Fall back to full Shares call to get thumbnails if drive/item ids don't exist.
                this.chooseSharesCall(item, strategy, true).then(driveItem => {
                    if (driveItem && driveItem.thumbnails && driveItem.thumbnails.length) {
                        const thumbnail = driveItem.thumbnails[0];
                        this.populateThumbnails(item, thumbnail);
                    }
                })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.hKd6C);
                    })
                    .then(() => resolve(item));
            } else {
                if (this.thumbnailApiEnabled && item.format !== MruFileFormat.News && item.format !== MruFileFormat.StreamsVideo) {
                    this.populateThumbnailWithUrl(item, this.getThumbnailUrlForItem(item.driveId, item.docId));
                    resolve(item);
                } else {
                    this.oneDriveGraphService.getThumbnailsForItem(item.driveId, item.docId, strategy, SchedulePriority.Medium)
                        .then(thumbnails => {
                            if (thumbnails && thumbnails.value && thumbnails.value.length) {
                                const thumbnail = thumbnails.value[0];
                                this.populateThumbnails(item, thumbnail);
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.ZYaIo);
                        })
                        .then(() => resolve(item));
                }
            }
        });
    }

    public waitOnInflations(): Promise<void> {
        const inflationCount = this.inflationsInProgress.length;
        return Promise.all(this.inflationsInProgress).then(() => {
            if (this.inflationsInProgress.length <= inflationCount) {
                return;
            }
            return this.waitOnInflations();
        });
    }

    private fetchItemInflationData(item: MruFileItem, strategy: FetchStrategy, fetchThumbnails: boolean, fetchAnalytics: boolean, mruAppType?: FileType): Promise<MruFileItem> {
        if (item.format === MruFileFormat.Recommended || item.format === MruFileFormat.OfficeFeedDocument || item.format === MruFileFormat.News) {
            // Always fetch thumbnails for recommended files
            return this.fetchRecommendedItemInflationData(item, strategy);
        }

        return new Promise<MruFileItem>(resolve => {
            const inflationPromises: Array<Promise<void>> = [];

            const shareControlEnabled = this.config.shareControlEnabled && item.fileType !== FileType.Sway;
            const fetchWacData = this.capabilitiesService.wacHostEnabledForFileType(item.fileType);

            if (item.format === MruFileFormat.Trending && item.driveId && item.docId) {
                if (this.tileViewEnabledForItem(item) && fetchThumbnails) {
                    if (fetchWacData) {
                        inflationPromises.push(this.chooseService(item).getItemWithWacDataAndThumbnails(item, strategy, false, SchedulePriority.Medium)
                            .then(driveItem => {
                                this.populateMruItemFromDriveItem(item, driveItem);
                                if (this.thumbnailApiEnabled && item.format !== MruFileFormat.News && item.format !== MruFileFormat.StreamsVideo) {
                                    this.populateThumbnailWithUrl(item, this.getThumbnailUrlForItem(item.driveId, item.docId));
                                }
                            })
                            .catch(error => {
                                this.globalErrorHandler.handleError(error, ErrorId.WF9ix);
                            }));
                    } else {
                        inflationPromises.push(this.oneDriveGraphService.getThumbnailsForItem(item.driveId, item.docId, strategy, SchedulePriority.Medium)
                            .then(thumbnails => {
                                if (thumbnails && thumbnails.value && thumbnails.value.length) {
                                    const thumbnail = thumbnails.value[0];
                                    this.populateThumbnails(item, thumbnail);
                                }
                            })
                            .catch(error => {
                                this.globalErrorHandler.handleError(error, ErrorId.iZlQF);
                            }));
                    }
                }

                if (item.fileType !== FileType.OneNote) {
                    inflationPromises.push(this.setupActivity(item, strategy));
                }

                if (shareControlEnabled || this.lifecycleEventsEnabled) {
                    inflationPromises.push(this.oneDriveGraphService.getItemWithIds(item.driveId, item.docId, 'sharepointIds,createdDateTime', strategy)
                        .then(driveItem => {
                            this.setupShareControl(item, driveItem);
                            this.setupLifecycleEvents(item, driveItem);
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.wUAkv);
                        }));
                }
            } else if ((this.tileViewEnabledForItem(item) || this.config.activityEnabled || this.config.permissionsEnabled || shareControlEnabled || fetchWacData)
                && VroomService.sharesFileTypes.has(item.fileType)
                && (item.sharingType === SharingType.ShareFlow || item.sharingType === SharingType.Modern || item.sharingType === SharingType.None)) {
                const mruV2DataPresent = this.checkAllDataIsPresentForInflation(item);
                if (mruV2DataPresent) {
                    this.setupLifecycleEvents(item, null);
                    const activityPromise = item.fileType !== FileType.OneNote ? this.setupActivity(item, strategy) : Promise.resolve();
                    const lifecycleAndActivityPromises = activityPromise.then(() => Promise.resolve());
                    inflationPromises.push(lifecycleAndActivityPromises);
                    if (fetchThumbnails && item.driveId && item.docId) {
                        inflationPromises.push(this.oneDriveGraphService.getThumbnailsForItem(item.driveId, item.docId, strategy, SchedulePriority.Medium)
                            .then(thumbnails => {
                                if (thumbnails && thumbnails.value && thumbnails.value.length) {
                                    const thumbnail = thumbnails.value[0];
                                    this.populateThumbnails(item, thumbnail);
                                }
                            })
                            .catch(error => {
                                this.globalErrorHandler.handleError(error, ErrorId.iZlQF);
                            }));
                    }
                }

                // We are getting sharing info from MRU, except in the case that we have a Sharepoint file (in which case we assume shared)
                // Because of this, if we have docId, itemId, createdDate, and sharepointIds we do not need to make the shares call to get any additional data
                if (!mruV2DataPresent) {
                    inflationPromises.push(this.chooseSharesCall(item, strategy, fetchThumbnails)
                        .then(driveItem => {
                            if (!driveItem) {
                                return Promise.resolve();
                            }

                            this.populateMruItemFromDriveItem(item, driveItem);
                            if (this.thumbnailApiEnabled && item.format !== MruFileFormat.News && item.format !== MruFileFormat.StreamsVideo) {
                                this.populateThumbnailWithUrl(item, this.getThumbnailUrlForItem(item.driveId, item.docId));
                            }
                            this.setupShareControl(item, driveItem);
                            this.setupLifecycleEvents(item, driveItem);

                            item.docId = driveItem.id;
                            if (driveItem.parentReference) {
                                item.driveId = driveItem.parentReference.driveId;
                            }

                            // Currently activity is not supported for OneNote notebooks.
                            return item.fileType !== FileType.OneNote ? this.setupActivity(item, strategy) : Promise.resolve();
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.EgyA3);
                        }));
                }
            }
            if (this.config.analyticsEnabled && fetchAnalytics) {
                // TODO: Add Scheduling support for Sway Analytics.
                const promiseAnalytics = this.analyticsService.getAnalytics(item, strategy)
                    .then(response => {
                        item.analytics = response;
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.gBfeR);
                    });
                inflationPromises.push(promiseAnalytics);
            }

            inflationPromises.push(...this.setupSwayData(item, strategy, fetchThumbnails));
            inflationPromises.push(...this.setupFormData(item, fetchThumbnails, mruAppType));
            Promise.all(inflationPromises).then(() => {
                item.inflationCompleted();
                resolve(item);
            }, () => {
                item.inflationCompleted();
                resolve(item);
            });
        });
    }

    private fetchRecommendedItemInflationData(item: MruFileItem, strategy: FetchStrategy): Promise<MruFileItem> {
        return new Promise<MruFileItem>(resolve => {
            if (item.driveId && item.docId) {
                this.fetchAndPopulateWacDataAndThumbnails(item, strategy)
                    .catch(driveDataError => {
                        this.globalErrorHandler.handleError(driveDataError, ErrorId.rm3f5);
                    })
                    .then(() => resolve(item));
            } else {
                resolve(item);
            }
        });
    }

    private getThumbnailUrlForItem(driveId: string, itemId: string): string | undefined {
        if (!driveId || !itemId) {
            return undefined;
        }

        const itemLocation = `drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}`;
        const url = `${itemLocation}/thumbnails/0/c400x99999/content?prefer=noredirect,closestavailablesize,extendCacheMaxAge`;
        return this.inMockMode ? `thumbnails?itemUrl=${encodeURIComponent(url)}` : url;
    }

    private fetchAndPopulateWacDataAndThumbnails(item: MruFileItem, strategy: FetchStrategy): Promise<void> {
        // TODO: Mock graph call with thumbnails
        if (this.capabilitiesService.wacHostEnabledForFileType(item.fileType) && !this.inMockMode) {
            return this.chooseService(item)
                .getItemWithWacDataAndThumbnails(item, strategy)
                .then(driveItem => {
                    this.populateMruItemFromDriveItem(item, driveItem);
                    if (this.thumbnailApiEnabled && item.format !== MruFileFormat.News && item.format !== MruFileFormat.StreamsVideo) {
                        this.populateThumbnailWithUrl(item, this.getThumbnailUrlForItem(item.driveId, item.docId));
                    }
                });
        }

        if (this.thumbnailApiEnabled && item.format !== MruFileFormat.News && item.format !== MruFileFormat.StreamsVideo) {
            this.populateThumbnailWithUrl(item, this.getThumbnailUrlForItem(item.driveId, item.docId));
            return Promise.resolve();
        } else {
            const thumbnailPromise = item.driveId && item.docId
                ? this.oneDriveGraphService.getThumbnailsForItem(item.driveId, item.docId, strategy)
                                           .then(thumbnails => thumbnails?.value)
                : this.chooseSharesCall(item, strategy, true).then(driveItem => driveItem?.thumbnails);

            return thumbnailPromise.then(thumbnails => {
                if (thumbnails?.length) {
                    const thumbnail = thumbnails[0];
                    this.populateThumbnails(item, thumbnail);
                }
            });
        }
    }

    private populateMruItemFromDriveItem(item: MruFileItem, driveItem: GraphDriveItem): void {
        if (!this.thumbnailApiEnabled && driveItem && driveItem.thumbnails && driveItem.thumbnails.length) {
            const thumbnail = driveItem.thumbnails[0];
            this.populateThumbnails(item, thumbnail);
        }
        if (driveItem && driveItem.openWith && driveItem.openWith.wac) {
            this.populateWopiHostData(item, driveItem);
        }
    }

    private populateThumbnails(item: MruFileItem, thumbnail: GraphThumbnailSet) {
        item.thumbnails = {
            small: thumbnail.small ? thumbnail.small.url : null,
            medium: thumbnail.medium ? thumbnail.medium.url : null,
            large: thumbnail.large ? thumbnail.large.url : null,
            recommendedThumbnail: (item.format === MruFileFormat.Recommended || item.format === MruFileFormat.News || item.format === MruFileFormat.OfficeFeedDocument) && thumbnail.large ?
                this.getRecommendedThumbnail(thumbnail.large.url) : null
        };
    }

    private populateThumbnailWithUrl(item: MruFileItem, thumbnail?: string) {
        if (!thumbnail) {
            return;
        }

        item.thumbnails = {
            large: thumbnail,
            recommendedThumbnail: thumbnail
        };
    }

    private populateWopiHostData(item: MruFileItem, driveItem: GraphDriveItem) {
        item.wopiData = VroomService.getWopiHostDataFromItem(driveItem);
    }

    private getRecommendedThumbnail(thumbnailUrl: string): string {
        const highRes = this.highResRecThumbnails;
        return thumbnailUrl.replace(/&width=\d+/, `&width=${highRes ? '999' : '350'}`).replace(/&height=\d+/, `&height=${highRes ? '999' : '400'}`);
    }

    private setupActivity(item: MruFileItem, strategy: FetchStrategy): Promise<void> {
        if (this.config.activityEnabled && !(item.format === MruFileFormat.Recent && this.config.mruActivityEnabled && this.config.onlyMruActivityEnabled)) {
            return this.vroomService.getDocumentActivity(item, strategy, SchedulePriority.Medium)
                .then(activityResponse => item.activities = activityResponse)
                .catch(error => {
                    this.globalErrorHandler.handleError(error, ErrorId.SluwI);
                }) as Promise<void>;
        }

        return Promise.resolve();
    }

    private chooseSharesCall(item: MruFileItem, strategy: FetchStrategy, fetchThumbnails: boolean): Promise<GraphDriveItem> {
        const url = item.canonicalUrl;
        // If document isn't from OneDrive or Sharepoint, then don't make Shares API call
        if (!url.match(SharePointUrlRegex) && !url.match(OneDriveConsumerUrlRegex)) {
            return Promise.resolve(undefined);
        }

        // Until we switch to MRU++ V2 (which should handle multi-geo documents), we will call the Vroom Shares API for dogfood docs so that we can get IDs
        // We assume if we are in this function, mru meta data is not present (sharepointSite, createdDateTime, docId, itemId)
        const service = this.chooseService(item);
        const fetchWacData = this.capabilitiesService.wacHostEnabledForFileType(item.fileType);
        return fetchThumbnails ? service.getSharedItemWithThumbnail(url, item.fileType, MruFileFormat[item.format], strategy, fetchWacData, false, SchedulePriority.Medium)
            : service.getSharedItemWithoutThumbnail(url, item.fileType, MruFileFormat[item.format], strategy, fetchWacData, undefined, false, SchedulePriority.Medium);
    }

    private chooseService(item: MruFileItem): OneDriveService {
        return item.isMsftDogfood ? this.vroomService : this.oneDriveGraphService;
    }

    private setupShareControl(item: MruFileItem, driveItem: GraphDriveItem) {
        if (driveItem && driveItem.sharepointIds && driveItem.sharepointIds.siteUrl) {
            item.sharepointIds.siteUrl = driveItem.sharepointIds.siteUrl;
        }
    }

    private setupLifecycleEvents(item: MruFileItem, driveItem: GraphDriveItem) {
        if (this.lifecycleEventsEnabled && !this.config.mruActivityEnabled) {
            let createdDateTime = item.createdDate;
            if (!createdDateTime && driveItem && driveItem.createdDateTime) {
                createdDateTime = new Date(driveItem.createdDateTime);
            }

            if (createdDateTime) {
                item.isNew = this.dateService.addHours(this.dateService.getCurrentDate(), -1 * this.config.isNewTimeWindowInHours).getTime() <= createdDateTime.getTime();
            }
        }
    }

    private setupFormData(item: MruFileItem, fetchThumbnails: boolean, mruAppType?: FileType): Array<Promise<void>> {
        const promises: Array<Promise<void>> = [];

        if (item.fileType !== FileType.Forms) {
            return promises;
        }

        if (mruAppType === FileType.Forms) {
            promises.push(new Promise((resolve, reject) => {
                const populateResponse = (responseCount: number = 0) => {
                    if (!item.metaData) {
                        item.metaData = {};
                    }
                    item.metaData.formsResponseCount = responseCount;
                };
                if (item.ownerId === this.clientStartupConfigService.config.objectId ||
                    this.clientStartupConfigService.config.authType === AuthType.MSA) {
                    // The user owns the form so we can use this api to get all of them
                    this.formsService.getMyFormsResponses()
                        .then(resp => {

                            const response = resp.find(countData => countData.id === item.serverDocId);
                            populateResponse(response?.responseCount);
                            resolve();
                        })
                        .catch(reject);
                } else {
                    // Otherwise use this one which will result in making individual calls for each form.
                    this.formsService.getForm(item.serverDocId, item.ownerType, item.ownerId)
                        .then(resp => {
                            const responseCount = resp ? resp.rowCount : null;
                            populateResponse(responseCount);
                            resolve();
                        })
                        .catch(reject);
                }
            }));
        }

        if (this.tileViewEnabledForItem(item) && fetchThumbnails) {
            promises.push(this.inflateThumbnails(item).then(() => {
                return;
            }));
        }
        return promises;
    }

    private setupSwayData(item: MruFileItem, strategy: FetchStrategy, fetchThumbnails: boolean): Array<Promise<void>> {
        const promises: Array<Promise<void>> = [];
        if (item.fileType !== FileType.Sway) {
            return promises;
        }

        if (this.tileViewEnabledForItem(item) && this.swayService.swayConfig.thumbnailsEnabled && fetchThumbnails) {
            promises.push(this.fetchSwayThumbnail(item, strategy));
        }

        item.url = this.urlAuthenticatorService.appendPvrAndUpnAuthParams(item.url);

        return promises;
    }

    // TODO: Add Scheduling support for Sway Thumbnail.
    private fetchSwayThumbnail(item: MruFileItem, strategy: FetchStrategy): Promise<void> {
        return this.swayService.getThumbnail(item.url, item.thumbnailInfo ? item.thumbnailInfo.thumbnailId : null, strategy)
            .then(response => {
                if (response) {
                    item.thumbnails = {
                        small: response,
                        medium: response,
                        large: response,
                        recommendedThumbnail: response
                    };
                }
            })
            .catch(error => {
                this.globalErrorHandler.handleError(error, ErrorId.cm5b1);
            });
    }

    private checkAllDataIsPresentForInflation(item: MruFileItem): boolean {
        return !!item.sharepointIds && !!item.createdDate && !!item.driveId && !!item.docId;
    }

    private tileViewEnabledForItem(item: MruFileItem): boolean {
        return this.config.tileViewEnabled || (item.fileType === FileType.Forms);
    }
}
